import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Activity, Play, Loader2, TrendingUp, AlertTriangle, CheckCircle2, GitBranch, Target, Download, Network } from 'lucide-react';
import * as d3 from 'd3';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProcessMiningFitGap = ({ project }) => {
  const [activeTab, setActiveTab] = useState('processes');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [diagramData, setDiagramData] = useState(null);
  const [generatingDiagram, setGeneratingDiagram] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const svgRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/process-mining/latest`);
      setResults(response.data.results);
    } catch (error) {
      console.log('No process mining results yet');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/process-mining/run`, {});
      setResults(response.data);
      // Auto-generate diagram after analysis
      generateDiagram();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const generateDiagram = async () => {
    setGeneratingDiagram(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/process-mining/generate-diagram`, {});
      setDiagramData(response.data);
    } catch (error) {
      console.error('Error generating diagram:', error);
    } finally {
      setGeneratingDiagram(false);
    }
  };

  const exportToPDF = async () => {
    setExportingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Process Mining & Fit-Gap Analysis Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Project: ${project.name}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Executive Summary
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Executive Summary', 15, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Total Processes Analyzed: ${results.summary.total_processes_analyzed}`, 15, yPosition);
      yPosition += 5;
      pdf.text(`Processes with Fit: ${results.summary.processes_with_fit}`, 15, yPosition);
      yPosition += 5;
      pdf.text(`Total Gaps Identified: ${results.summary.total_gaps_identified}`, 15, yPosition);
      yPosition += 5;
      pdf.text(`Overall Fit Score: ${results.summary.overall_fit_score}%`, 15, yPosition);
      yPosition += 5;
      pdf.text(`Estimated Redesign Effort: ${results.summary.estimated_redesign_effort_days} days`, 15, yPosition);
      yPosition += 12;

      // Process Diagram
      if (svgRef.current && diagramData) {
        pdf.addPage();
        yPosition = 20;
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Process Flow Diagram', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        const canvas = await html2canvas(svgRef.current, { scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 30;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 15, yPosition, imgWidth, Math.min(imgHeight, pageHeight - 40));
      }

      // Fit-Gap Analysis
      pdf.addPage();
      yPosition = 20;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Fit-Gap Analysis', 15, yPosition);
      yPosition += 10;

      results.fit_gap_analysis.forEach((analysis, idx) => {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${analysis.process_name} (${analysis.fit_level})`, 15, yPosition);
        yPosition += 6;

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Match: ${analysis.match_percentage}% | Gaps: ${analysis.gaps.length} | Effort: ${analysis.estimated_redesign_effort_days} days`, 15, yPosition);
        yPosition += 8;

        if (analysis.gaps.length > 0 && yPosition < pageHeight - 30) {
          pdf.setFontSize(8);
          analysis.gaps.slice(0, 3).forEach(gap => {
            if (yPosition < pageHeight - 10) {
              pdf.text(`- ${gap.type}: ${gap.details}`, 20, yPosition);
              yPosition += 5;
            }
          });
        }
        yPosition += 5;
      });

      // AI Recommendations
      if (results.ai_recommendations.process_redesign_priorities.length > 0) {
        pdf.addPage();
        yPosition = 20;
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('AI Recommendations', 15, yPosition);
        yPosition += 10;

        results.ai_recommendations.process_redesign_priorities.slice(0, 5).forEach(priority => {
          if (yPosition > pageHeight - 35) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text(priority.process, 15, yPosition);
          yPosition += 6;

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(priority.redesign_approach, pageWidth - 30);
          lines.forEach(line => {
            if (yPosition < pageHeight - 10) {
              pdf.text(line, 15, yPosition);
              yPosition += 5;
            }
          });
          yPosition += 8;
        });
      }

      // Save PDF
      pdf.save(`AURA_PM-FGA_${project.name}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  useEffect(() => {
    if (diagramData && svgRef.current) {
      renderD3Diagram();
    }
  }, [diagramData]);

  const renderD3Diagram = () => {
    if (!diagramData || !svgRef.current) return;

    // Clear existing content
    d3.select(svgRef.current).selectAll('*').remove();

    const width = 1000;
    const height = 700;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto; background: #f8fafc; border-radius: 12px;');

    // Create force simulation
    const simulation = d3.forceSimulation(diagramData.nodes)
      .force('link', d3.forceLink(diagramData.edges).id(d => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(40));

    // Add zoom behavior
    const g = svg.append('g');
    svg.call(d3.zoom()
      .extent([[0, 0], [width, height]])
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      }));

    // Draw edges
    const link = g.append('g')
      .selectAll('line')
      .data(diagramData.edges)
      .join('line')
      .attr('stroke', d => d.type === 'bottleneck' ? '#EF4444' : '#94A3B8')
      .attr('stroke-width', d => Math.sqrt(d.weight) / 30 || 2)
      .attr('stroke-opacity', 0.6);

    // Draw nodes
    const node = g.append('g')
      .selectAll('g')
      .data(diagramData.nodes)
      .join('g')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    node.append('circle')
      .attr('r', d => d.size || 15)
      .attr('fill', d => d.color || '#3B82F6')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    node.append('text')
      .text(d => d.label)
      .attr('x', 0)
      .attr('y', d => (d.size || 15) + 15)
      .attr('text-anchor', 'middle')
      .attr('fill', '#1F2937')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold');

    // Add tooltips
    node.append('title')
      .text(d => {
        let tooltip = d.label;
        if (d.metadata) {
          if (d.metadata.process) tooltip += `\nProcess: ${d.metadata.process}`;
          if (d.metadata.frequency) tooltip += `\nFrequency: ${d.metadata.frequency}`;
          if (d.metadata.fit_level) tooltip += `\nFit Level: ${d.metadata.fit_level}`;
          if (d.metadata.is_bottleneck) tooltip += `\n⚠️ Bottleneck Detected`;
        }
        return tooltip;
      });

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>;

  const getFitColor = (fitLevel) => {
    switch(fitLevel) {
      case 'FIT': return 'green';
      case 'PARTIAL_FIT': return 'yellow';
      case 'GAP': return 'red';
      default: return 'gray';
    }
  };

  return (
    <div className="space-y-6" ref={contentRef}>
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Process Mining & Fit-Gap Analyzer</h2>
            <p className="text-gray-600">Reconstruct actual processes and compare to S/4HANA best practices</p>
            
            {/* SAP System Indicator */}
            {results && results.sap_system_analyzed && results.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Activity className="w-4 h-4 text-indigo-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-indigo-600">{results.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({results.sap_system_analyzed.host})</span>
                </div>
                {results.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {results && (
              <button 
                onClick={exportToPDF} 
                disabled={exportingPDF} 
                className="bg-green-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
              >
                {exportingPDF ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Export PDF</span>
                  </>
                )}
              </button>
            )}
            <button 
              onClick={runAnalysis} 
              disabled={analyzing} 
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Run Analysis</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {!results && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Activity className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Analysis Results</h3>
          <p className="text-gray-600 mb-6">Run process mining to discover actual business flows</p>
          <button onClick={runAnalysis} className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2">
            <Play className="w-5 h-5" />
            <span>Start Analysis</span>
          </button>
        </div>
      )}

      {results && (
        <>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Activity className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{results.summary.total_processes_analyzed}</div>
              <div className="text-blue-100 text-sm">Processes Found</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <CheckCircle2 className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{results.summary.processes_with_fit}</div>
              <div className="text-green-100 text-sm">Fit with S/4</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{results.summary.total_gaps_identified}</div>
              <div className="text-red-100 text-sm">Gaps Found</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <TrendingUp className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{results.summary.overall_fit_score}%</div>
              <div className="text-purple-100 text-sm">Overall Fit</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'diagram', label: 'Process Diagram', icon: Network },
                { id: 'processes', label: 'Process Flows', icon: Activity },
                { id: 'fitgap', label: 'Fit-Gap Matrix', icon: Target },
                { id: 'variants', label: 'Variants', icon: GitBranch },
                { id: 'recommendations', label: 'AI Recommendations', icon: TrendingUp }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button 
                    key={tab.id} 
                    onClick={() => setActiveTab(tab.id)} 
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Real Data Indicator Banner - Shows on all tabs */}
              {results && results.real_metrics && (
                <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-gray-900">
                        Real SAP Data: {results.real_metrics.fiori_apps_detected} Fiori apps detected
                      </span>
                      <span className="text-xs text-gray-600">
                        • {results.real_metrics.process_services_found} process services analyzed
                      </span>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                      📊 Real Data
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {results.real_metrics.note}
                  </p>
                </div>
              )}
              
              {activeTab === 'diagram' && (
                <div className="space-y-4">
                  {!diagramData && !generatingDiagram && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8 text-center">
                      <Network className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Generate AI-Powered Process Diagram</h3>
                      <p className="text-gray-600 mb-4">Create an interactive visualization of your process flows</p>
                      <button 
                        onClick={generateDiagram} 
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2"
                      >
                        <Network className="w-5 h-5" />
                        <span>Generate Diagram</span>
                      </button>
                    </div>
                  )}

                  {generatingDiagram && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                      <p className="text-gray-600">Generating AI-powered process diagram...</p>
                    </div>
                  )}

                  {diagramData && (
                    <div>
                      <div className="mb-4 flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">Interactive Process Flow Diagram</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {diagramData.ai_generated && (
                              <span className="inline-flex items-center space-x-1 text-purple-600">
                                <span className="inline-block w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                                <span>AI-Generated with {diagramData.model}</span>
                              </span>
                            )}
                            {!diagramData.ai_generated && "Rule-based diagram"}
                          </p>
                        </div>
                        <button 
                          onClick={generateDiagram} 
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
                        >
                          <Network className="w-4 h-4" />
                          <span>Regenerate</span>
                        </button>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                        <svg ref={svgRef}></svg>
                      </div>

                      {diagramData.insights && diagramData.insights.length > 0 && (
                        <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                            <TrendingUp className="w-5 h-5 text-blue-600" />
                            <span>Key Insights</span>
                          </h4>
                          <ul className="space-y-2">
                            {diagramData.insights.map((insight, idx) => {
                              // Handle both string and object formats
                              const insightText = typeof insight === 'string' ? insight : insight.text || JSON.stringify(insight);
                              return (
                                <li key={idx} className="text-sm text-gray-700 flex items-start space-x-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                  <span>{insightText}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      <div className="mt-4 text-center text-xs text-gray-500">
                        💡 Tip: Drag nodes to rearrange, scroll to zoom, hover for details
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'processes' && (
                <div className="space-y-4">
                  {results.process_flows.map((flow) => (
                    <div key={flow.flow_id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{flow.process_name}</h4>
                          <p className="text-sm text-gray-600">Flow ID: {flow.flow_id} | Frequency: {flow.frequency.toLocaleString()} executions</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">{flow.success_rate * 100}%</div>
                          <div className="text-xs text-gray-500">Success Rate</div>
                        </div>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4 mb-4">
                        <h5 className="font-semibold text-gray-900 mb-3">Process Sequence</h5>
                        <div className="flex items-center space-x-2 overflow-x-auto">
                          {flow.sequence.map((tcode, idx) => (
                            <React.Fragment key={idx}>
                              <div className="bg-white border-2 border-blue-300 rounded-lg px-4 py-2 whitespace-nowrap">
                                <div className="font-semibold text-gray-900">{tcode}</div>
                              </div>
                              {idx < flow.sequence.length - 1 && (
                                <div className="text-blue-600 font-bold">→</div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div><span className="text-gray-600">Avg Duration:</span> <span className="font-semibold">{(flow.avg_duration_hours / 24).toFixed(1)} days</span></div>
                        <div><span className="text-gray-600">Volume/Month:</span> <span className="font-semibold">{Math.round(flow.volume_per_month).toLocaleString()}</span></div>
                      </div>

                      {flow.bottlenecks.length > 0 && (
                        <div className="mt-4 bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-2">Bottlenecks Detected</h5>
                          {flow.bottlenecks.map((bn, i) => (
                            <div key={i} className="text-sm text-gray-700 mb-1">
                              <span className="font-semibold">{bn.tcode}:</span> {bn.reason} (avg wait: {bn.avg_wait_time_hours}h)
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'fitgap' && (
                <div className="space-y-4">
                  {results.fit_gap_analysis.map((analysis) => (
                    <div key={analysis.flow_id} className={`border-2 rounded-xl p-6 ${
                      analysis.fit_level === 'FIT' ? 'border-green-200 bg-green-50' :
                      analysis.fit_level === 'PARTIAL_FIT' ? 'border-yellow-200 bg-yellow-50' :
                      'border-red-200 bg-red-50'
                    }`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-xl font-bold text-gray-900">{analysis.process_name}</h4>
                            <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                              analysis.fit_level === 'FIT' ? 'bg-green-100 text-green-700' :
                              analysis.fit_level === 'PARTIAL_FIT' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>{analysis.fit_level.replace('_', ' ')}</span>
                          </div>
                          <p className="text-sm text-gray-600">Match: {analysis.match_percentage}% | Gaps: {analysis.gaps.length} | Effort: {analysis.estimated_redesign_effort_days} days</p>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-white rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-2">Actual Sequence</h5>
                          <div className="flex flex-wrap gap-2">
                            {analysis.actual_sequence.map((t, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{t}</span>
                            ))}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-2">Best Practice</h5>
                          <div className="flex flex-wrap gap-2">
                            {analysis.best_practice_sequence.map((t, i) => (
                              <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">{t}</span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {analysis.gaps.length > 0 && (
                        <div className="bg-white rounded-lg p-4 mb-4">
                          <h5 className="font-semibold text-gray-900 mb-3">Identified Gaps</h5>
                          <div className="space-y-2">
                            {analysis.gaps.map((gap, i) => (
                              <div key={i} className="flex items-start space-x-2">
                                <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-gray-900">{gap.type}</p>
                                  <p className="text-xs text-gray-600">{gap.details}</p>
                                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    gap.impact === 'High' ? 'bg-red-100 text-red-700' :
                                    gap.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>Impact: {gap.impact}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {analysis.recommendations.length > 0 && (
                        <div className="bg-white rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-3">Recommendations</h5>
                          <ul className="space-y-2">
                            {analysis.recommendations.map((rec, i) => (
                              <li key={i} className="text-sm text-gray-700 flex items-start space-x-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {analysis.s4_innovations.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <span className="text-sm font-semibold text-gray-700">S/4 Innovations:</span>
                          {analysis.s4_innovations.map((innovation, i) => (
                            <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">{innovation}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'variants' && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Process Variants Detected</h4>
                    <p className="text-sm text-gray-600">{results.summary.total_variants} variant(s) found - opportunities for standardization</p>
                  </div>

                  {results.process_variants.map((variant) => (
                    <div key={variant.variant_id} className="bg-white border-2 border-yellow-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{variant.variant_name}</h4>
                          <p className="text-sm text-gray-600">Base: {variant.base_process} | Frequency: {variant.frequency.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-orange-600">{variant.complexity_score}</div>
                          <div className="text-xs text-gray-500">Complexity</div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <h5 className="font-semibold text-gray-900 mb-2">Variant Sequence</h5>
                        <div className="flex items-center space-x-2 overflow-x-auto">
                          {variant.sequence.map((tcode, idx) => (
                            <React.Fragment key={idx}>
                              <span className="px-3 py-1 bg-white border-2 border-gray-300 rounded text-sm font-semibold whitespace-nowrap">{tcode}</span>
                              {idx < variant.sequence.length - 1 && <span className="text-gray-400">→</span>}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Deviation Type</p>
                          <p className="font-semibold text-gray-900">{variant.deviation_type}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Deviation Reason</p>
                          <p className="font-semibold text-gray-900">{variant.deviation_reason}</p>
                        </div>
                      </div>

                      <div className="mt-4 bg-green-50 border-2 border-green-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Recommended Action:</p>
                        <p className="text-sm text-gray-700">{variant.recommended_action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'recommendations' && (
                <div className="space-y-6">
                  {results.ai_recommendations.process_redesign_priorities.length > 0 && (
                    <div className="bg-white rounded-xl shadow-md p-6">
                      <h4 className="text-xl font-bold mb-4">Process Redesign Priorities</h4>
                      <div className="space-y-4">
                        {results.ai_recommendations.process_redesign_priorities.map((priority, idx) => (
                          <div key={idx} className="border-2 border-blue-200 bg-blue-50 rounded-lg p-5">
                            <div className="flex items-start justify-between mb-3">
                              <h5 className="text-lg font-bold text-gray-900">{priority.process}</h5>
                              <span className="text-lg font-bold text-blue-600">{priority.effort_estimate}</span>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-xs text-gray-600 mb-1">Current State</p>
                                <p className="text-sm text-gray-900">{priority.current_state}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 mb-1">Target State</p>
                                <p className="text-sm text-gray-900">{priority.target_state}</p>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 mb-3">
                              <p className="text-xs text-gray-600 mb-2">Redesign Approach</p>
                              <p className="text-sm font-semibold text-gray-900">{priority.redesign_approach}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <p className="text-xs text-gray-600 mb-1">Business Benefit</p>
                              <p className="text-sm text-green-700 font-semibold">{priority.business_benefit}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.ai_recommendations.s4_innovation_adoption.length > 0 && (
                    <div className="bg-white rounded-xl shadow-md p-6">
                      <h4 className="text-xl font-bold mb-4">S/4HANA Innovation Opportunities</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {results.ai_recommendations.s4_innovation_adoption.map((innovation, idx) => (
                          <div key={idx} className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                            <h5 className="font-semibold text-gray-900 mb-2">{innovation.innovation}</h5>
                            <p className="text-sm text-gray-600 mb-2">{innovation.description}</p>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-500">Applies to: {innovation.applicable_to}</span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-semibold">{innovation.adoption_effort}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.ai_recommendations.automation_candidates.length > 0 && (
                    <div className="bg-white rounded-xl shadow-md p-6">
                      <h4 className="text-xl font-bold mb-4">Automation Opportunities</h4>
                      <div className="space-y-3">
                        {results.ai_recommendations.automation_candidates.map((auto, idx) => (
                          <div key={idx} className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-gray-900">{auto.process}</h5>
                              <span className="text-lg font-bold text-green-600">{auto.estimated_savings}</span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{auto.automation_opportunity}</p>
                            <div className="flex flex-wrap gap-2">
                              {auto.tools.map((tool, i) => (
                                <span key={i} className="px-2 py-1 bg-white border border-green-300 rounded text-xs">{tool}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProcessMiningFitGap;
